import { SECTION_KEYS } from '../../../constants.js';

// Validation functions
export function validateSection(sectionKey, state) {
  const errors = [];
  const v = state[sectionKey];

  switch (sectionKey) {
    case "personal":
      
      if (!v.title) errors.push("Title is required");
      if (!v.givenNames?.trim()) errors.push("Given Names is required");
      if (!v.familyName?.trim()) errors.push("Family Name is required");

      if (!v.dateOfBirth) {
        errors.push("Date of Birth is required");
      } else if (!isAtLeast18(v.dateOfBirth)) {
        errors.push("Date of Birth must show you are at least 17 years old");
      }

      if (!v.gender) errors.push("Gender is required");
      if (!v.countryOfBirth) errors.push("Country of Birth is required");
      if (!v.citizenship) errors.push("Citizenship is required");

      if (!v.passportNumber?.trim()) errors.push("Passport Number is required");

      if (!v.passportExpiry) {
        errors.push("Passport Expiry Date is required");
      } else if (!isFutureDate(v.passportExpiry)) {
        errors.push("Passport Expiry Date must be in the future");
      }

      if (!v.passportCountryOfIssue) errors.push("Passport Country of Issue is required");
      if (!v.passportCopy) errors.push("Passport Copy is required");

      if (!v.inAustraliaAtApplication) errors.push("In Australia at time of application is required");

      if (!v.address?.trim()) errors.push("Address is required");
      if (!v.suburb?.trim()) errors.push("Suburb is required");
      if (!v.state) errors.push("State/Territory is required");
      if (!v.otherState && v.state === "OTHER") errors.push("Your State/Territory is required");
      if (!v.country) errors.push("Country is required");
      if (!v.postcode?.trim()) errors.push("Postcode is required");
      if (!v.mobilePhone?.trim()) errors.push("Mobile Phone is required");
      if (!v.emergencyContactName?.trim()) errors.push("Emergency Contact Name is required");
      if (!v.emergencyContactRelation?.trim()) errors.push("Emergency Contact Relation is required");
      if (!v.emergencyContactNumber?.trim()) errors.push("Emergency Contact Number is required");

      if (!v.email?.trim()) {
        errors.push("Email is required");
      } else if (!/\S+@\S+\.\S+/.test(v.email)) {
        errors.push("Please enter a valid email address");
      }
      if (v.previouslyStudiedAtCIET === "Yes" && !v.studentId?.trim()) {
        errors.push("Student ID is required if you have previously studied at CIET");
      }
      break;

    case "campusCourse":
      if (!v.campus) errors.push("Campus selection is required");
      if (v.courses.length == 0) errors.push("Course selection is required");
      break;

    case "intake":
      if (!v.month) errors.push("Intake month is required");
      if (!v.year) errors.push("Intake year is required");
      if (v.year && (isNaN(v.year) || v.year.length !== 4)) {
        errors.push("Please enter a valid 4-digit year");
      }
      break;

    case "visa":
      // Optional validation - could add specific visa requirements
      // 1. If user selects any option other than "No Visa Yet"
      if ((v.visaStatus && v.visaStatus =="Select") || (v.visaStatus && v.visaStatus !== "No Visa Yet (Offshore Application)")) {
          if (!v.visaType) errors.push("Visa Type/ SubClass is required");
          if (!v.visaExpiryDate) {
            errors.push("Visa Expiry Date is required");
          } else if (!isValidDate(v.visaExpiryDate)) {
            errors.push("Visa Expiry Date is invalid");
          } else if (!isFutureDate(v.visaExpiryDate)) {
            errors.push("Visa Expiry Date must be in the future");
          }
          if (!v.currentVisaCopy) errors.push("Current/Previous Visa Copy is required");

        // 2. Previous Applications
        if (v.appliedBefore === "Yes") {
          if (!v.granted) errors.push("Previous application result is required");

          // 3. If refused, check details
          if (v.granted === "No") {
            if (!v.refusalDate) {
              errors.push("Refusal Date is required");
            } else if (!isValidDate(v.refusalDate)) {
              errors.push("Refusal Date is invalid");
            } else if (new Date(v.refusalDate) > new Date()) {
              errors.push("Refusal Date cannot be in the future");
            }

            if (!v.refusalReason?.trim()) errors.push("Refusal Reason is required");
            if (!v.refusalLetter) errors.push("Refusal Letter upload is required");
          }
        }

        // 4. Embassy/DHA Office is always required
        if (!v.embassyOffice) errors.push("Embassy/DHA Office is required");
      }
      break;

    case "languageCulture":
      if (!v) break;
      if (v.languageAtHome === "Other" && !v.otherLanguage?.trim()) {
        errors.push("Please specify the other language");
      }
      if (!v.country) errors.push("Country of Birth is required");
      if (v.englishTest != "") {
        if (!v.currentEnglishCopy) errors.push("Current/Previous English Scores upload is required");
        if (!v.overallScore) errors.push("Overall Score is required");
        if (!v.testDate) {
          errors.push("Test Date is required");
        } else if (!isValidDate(v.testDate)) {
          errors.push("Test Date is invalid");
        } else if (new Date(v.testDate) > new Date()) {
          errors.push("Test Date cannot be in the future");
        }
      }
      break;

    case "disability":
      // No required fields
      break;

    case "schoolingQualifications":
      // Validate prior education entries
      if (v.currentlyEnrolledAnyStudies === "Yes") {
        if (!v.currentProviderName?.trim()) errors.push("Current institution name is required");
        if (!v.currentProviderCourse?.trim()) errors.push("Current course name is required");
        if (!v.currentProviderContact?.trim()) errors.push("Current institution contact/address is required");
      }

      if (!v.priorEducationList || v.priorEducationList.length === 0) {
        errors.push("At least one prior education entry is required");
      } else {
        v.priorEducationList.forEach((edu, index) => {
          if (!edu.institution?.trim()) errors.push(`Prior education ${index + 1}: Institution is required`);
          if (!edu.qualification?.trim()) errors.push(`Prior education ${index + 1}: Qualification is required`);
          if (!edu.location?.trim()) errors.push(`Prior education ${index + 1}: Location is required`);
          if (!edu.yearCompleted?.trim()) errors.push(`Prior education ${index + 1}: Year completed is required`);
        });
      }

      if (!v.transcripts || v.transcripts.length === 0) errors.push("At least one academic transcript or certificate must be uploaded");

      if (v.completedQualificationsInEnglish === "Yes" && (!v.completedLevels || v.completedLevels.length === 0)) {
        errors.push("At least one English qualification level must be selected");
      }
      break;

    case "oshc":
    case "employmentStudyReason":
      break;
    case "additionalServices":
      if (v.airportPickup === "Yes" && !v.arrivalDetails?.trim()) {
        errors.push("Airport Pick Up Details are required");
      }
      break;

    case "rplCredit":
      break;
    case "usi":
      if (v.usiExemptionProvided === "Yes" && !v.usiExemptionLetter) {
        errors.push("USI Exemption Letter is required");
      }
      break;

    case "declaration":
      if (!v.confirmTrueAndCorrect) {
        errors.push("You must confirm that the information provided is true and correct");
      }
      if (!v.agreePrivacyNotice) {
        errors.push("You must agree to the Privacy Notice");
      }
      if (!v.signatureName?.trim()) {
        errors.push("Signature name is required");
      }
      if (!v.signature?.trim()) {
        errors.push("Signature is required");
      }
      if (!v.signatureDate) {
        errors.push("Signature date is required");
      }
      for(const key of Object.keys(v.checklist)) {
        if (!v.checklist[key]) {
          errors.push(`Checklist item "${prettyChecklist(key)}" must be completed`);
        }
      }
      break;

    default:
      // No validation for unknown sections
      break;
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
function isAtLeast18(dobString) {
  if (!dobString) return false;
  const dob = new Date(dobString);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const hasHadBirthday =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

  if (!hasHadBirthday) age -= 1;
  return age >= 17;
}

function isFutureDate(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date > today;
}
export function validateAll(state, sectionKeys) {
  const problems = [];
  
  for (const key of sectionKeys) {
    const validation = validateSection(key, state);
    if (!validation.valid) {
      problems.push({
        key,
        errors: validation.errors
      });
    }
  }

  return {
    valid: problems.length === 0,
    problems
  };
}

export function labelForSection(key) {
  const labels = {
    personal: "Personal Details",
    campusCourse: "Campus & Course",
    intake: "Intake Date",
    visa: "Visa Status",
    languageCulture: "Language & Culture",
    disability: "Disability Support",
    schoolingQualifications: "Education Background",
    oshc: "Health Cover",
    employmentStudyReason: "Employment & Study",
    additionalServices: "Additional Services",
    rplCredit: "RPL & Credit Transfer",
    usi: "Unique Student Identifier (USI)",
    declaration: "Declaration & Privacy"
  };

  return labels[key] || key;
}

export function prettyChecklist(key) {
  const checklistLabels = {
    formCompleted: "Completed all sections of the application form",
    passportAttached: "Attached certified copies of my passport",
    visaAttachedIfApplicable: "Attached current Australian visa (if applicable)",
    englishEvidenceAttached: "Attached evidence of English language proficiency",
    gsFormAttached: "Attached completed GS form",
    transcriptsAttached: "Attached certified copies of academic transcript(s) and certificate(s) translated into English",
    readSignedDeclaration: "Read and signed the student declaration",
    financialDocsAttached: "Financial Documents"
  };

  return checklistLabels[key] || key;
}

// Utility to get section icon
export function getSectionIcon(key) {
  const icons = {
    personal: "User",
    campusCourse: "GraduationCap",
    intake: "Calendar",
    visa: "FileText",
    languageCulture: "Globe",
    disability: "Accessibility",
    schoolingQualifications: "School",
    oshc: "Heart",
    employmentStudyReason: "Briefcase",
    additionalServices: "Home",
    rplCredit: "Award",
    usi: "Shield",
    declaration: "ScrollText"
  };

  return icons[key] || "FileText";
}

// Progress calculation
export function calculateProgress(currentStep, totalSteps) {
  return Math.round((currentStep / (totalSteps - 1)) * 100);
}

// Form persistence helpers
export function saveFormProgress(state) {
  try {
    // In a real application, this would save to localStorage or sessionStorage
    // But Claude artifacts don't support browser storage, so we'll just return
    return true;
  } catch (error) {
    return false;
  }
}

export function loadFormProgress() {
  try {
    // In a real application, this would load from localStorage or sessionStorage
    return null;
  } catch (error) {
    return null;
  }
}

// File handling utilities
export function validateFileType(file, allowedTypes = []) {
  if (!file) return true; // Optional files
  
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  
  // Common document types
  const defaultAllowed = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  
  const allowed = allowedTypes.length > 0 ? allowedTypes : defaultAllowed;
  
  return allowed.some(type => 
    fileType.includes(type) || fileName.endsWith(type.split('/')[1])
  );
}

export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Date utilities
export function getCurrentDate() {
  return new Date().toISOString().split('T')[0];
}

export function isValidDate(dateString) {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
}

// Form submission helpers
export function prepareSubmissionData(state) {
  // Clean and prepare data for submission
  const submissionData = {
    ...state,
    submittedAt: new Date().toISOString(),
    version: "v2.0"
  };

  // Remove any file objects that can't be serialized
  // In a real app, files would be uploaded separately
  const cleanData = JSON.parse(JSON.stringify(submissionData, (key, value) => {
    if (value instanceof File) {
      return {
        name: value.name,
        size: value.size,
        type: value.type,
        lastModified: value.lastModified
      };
    }
    return value;
  }));

  return cleanData;
}